
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
const BRAND_CREAM = "#FAF7F2";

export default function WillkommenPocketCoach() {
  const [searchParams] = useSearchParams();
  const fnFromUrl = useMemo(() => {
    const raw = (searchParams.get("fn") || "").trim();
    const cleaned = raw.replace(/[^\wÀ-ÿäöüÄÖÜß\-\s.]/g, "").slice(0, 80);
    return cleaned || "";
  }, [searchParams]);

  const [firstName, setFirstName] = useState(fnFromUrl);
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  // 2k-N: "neu" = Konto angelegt und angemeldet; "bekannt" = Login-Link per Mail.
  const [checkInbox, setCheckInbox] = useState(null);
  useEffect(() => {
    try {
      document.title = "Dein Zugang zum Flex4Success PocketCoach";
      const m = document.createElement("meta");
      m.name = "robots";
      m.content = "noindex, nofollow";
      document.head.appendChild(m);
    } catch (_e) {  }
    try {
      if (typeof window.gtag === "function") {
        window.gtag("event", "pocket_coach_willkommen_view", {
          has_firstname: Boolean(fnFromUrl),
        });
      }
    } catch (_e) {  }
  }, []);

  useEffect(() => {
    if (fnFromUrl && !firstName) setFirstName(fnFromUrl);
  }, [fnFromUrl]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail || !trimmedEmail.includes("@")) {
      setError("Bitte eine gültige Mail-Adresse eingeben.");
      return;
    }
    if (!consent) {
      setError("Bitte Einverständnis bestätigen.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/invite/pocket-coach/accept", {
        email: trimmedEmail,
        first_name: firstName.trim() || null,
        consent: true,
      });
      try {
        if (typeof window.gtag === "function") {
          window.gtag("event", "pocket_coach_invite_submit", {
            created: Boolean(data?.created),
          });
        }
      } catch (_e) {  }
      if (data?.check_inbox) {
        setCheckInbox("bekannt");
        setSubmitting(false);
        return;
      }
      setCheckInbox("neu");
      setSubmitting(false);
    } catch (_err) {
      setError("Da ist etwas schiefgegangen. Bitte noch einmal versuchen.");
      setSubmitting(false);
    }
  }

  const greetingName = (firstName || fnFromUrl || "").trim();

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: BRAND_CREAM }}
      data-testid="invite-pocket-coach-page"
    >
      {}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-[520px]"
        style={{
          background:
            "radial-gradient(ellipse at 50% 0%, rgba(243,101,29,0.08) 0%, rgba(243,101,29,0) 55%)",
        }}
      />

      <div className="relative mx-auto w-full max-w-[560px] px-5 py-10 sm:py-14">
        {}
        <div className="flex justify-center">
          <span
            className="inline-flex items-center gap-2 rounded-full bg-white border border-amber-300/70 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.15em] text-amber-700 shadow-sm"
            data-testid="invite-tagline"
          >
            <span
              className="w-1.5 h-1.5 rounded-full bg-amber-500"
              aria-hidden="true"
            />
            Persönliche Einladung · PocketCoach
          </span>
        </div>

        {}
        <div
          className="mx-auto mt-7 h-44 w-44 overflow-hidden rounded-full shadow-2xl ring-4 ring-white"
          data-testid="invite-andreas-photo"
        >
          <img
            src="/images/andreas-portrait.jpg"
            alt="Andreas Jung"
            className="h-full w-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = "none";
              const parent = e.currentTarget.parentElement;
              if (parent && !parent.querySelector("[data-fallback]")) {
                const div = document.createElement("div");
                div.setAttribute("data-fallback", "true");
                div.className =
                  "flex h-full w-full items-center justify-center bg-[var(--fts-primary-deep)] text-4xl font-bold text-white";
                div.textContent = "AJ";
                parent.appendChild(div);
              }
            }}
          />
        </div>

        {}
        <h1
          className="mt-7 text-center text-4xl sm:text-5xl font-extrabold leading-[1.1] tracking-tight text-[var(--fts-text-primary)]"
          data-testid="invite-headline"
        >
          {greetingName ? (
            <>
              <span>{greetingName},</span>
              <br />
              <span>
                dein{" "}
                <span className="relative inline-block">
                  Zugang
                  <span
                    className="absolute left-0 right-0 -bottom-0.5 h-[3px] rounded-full bg-amber-400"
                    aria-hidden="true"
                  />
                </span>{" "}
                zum Flex4Success PocketCoach
              </span>
            </>
          ) : (
            <>
              Dein{" "}
              <span className="relative inline-block">
                Zugang
                <span
                  className="absolute left-0 right-0 -bottom-0.5 h-[3px] rounded-full bg-amber-400"
                  aria-hidden="true"
                />
              </span>{" "}
              zum Flex4Success PocketCoach
            </>
          )}
        </h1>

        {}
        <div
          className="mt-6 space-y-4 text-center text-lg leading-relaxed text-slate-700"
          data-testid="invite-context"
        >
          <p>
            Vier Muster. Die meisten Gespräche, die sich festfahren, drehen
            sich um dieselben vier. Der PocketCoach zeigt dir, welches dir
            gerade gegenübersteht — und was du sagst, wenn es darauf ankommt.
          </p>
        </div>

        {}
        <div
          className="mt-10 rounded-2xl bg-amber-50/60 border border-amber-100 px-5 py-5"
          data-testid="invite-what-it-is"
        >
          <h2 className="text-base font-bold text-[var(--fts-text-primary)]">
            Was du damit machst
          </h2>
          <p className="mt-2 text-base leading-relaxed text-slate-700">
            Im Browser auf Desktop und Handy — überall dieselbe Oberfläche.
            Du öffnest es und bist drin.
          </p>
        </div>

        {}
        <ul className="mt-7 space-y-4" data-testid="invite-outcomes">
          {[
            "Deine Fokus-Themen sortieren, sobald sie aufkommen — statt sie liegenzulassen.",
            "Für Entscheidungen vor einem Gespräch den passenden Impuls holen.",
            "Nach einem anstrengenden Tag in zwei Minuten runterkommen und neu ausrichten.",
          ].map((text, idx) => (
            <li key={idx} className="flex gap-3">
              <CheckCircle2
                className="mt-0.5 h-6 w-6 flex-shrink-0 text-[var(--fts-primary-deep)]"
                aria-hidden="true"
              />
              <span className="text-base leading-relaxed text-slate-800">
                {text}
              </span>
            </li>
          ))}
        </ul>

        {checkInbox === "bekannt" && (
          <div className="mt-10 rounded-2xl bg-white border border-amber-200 px-5 py-6 text-center" data-testid="invite-check-inbox">
            <p className="text-lg font-bold text-[var(--fts-text-primary)]">Schau in dein Postfach.</p>
            <p className="mt-2 text-sm text-slate-600">Klick den Link in der Mail — dann bist du drin.</p>
          </div>
        )}
        {checkInbox === "neu" && (
          <div className="mt-10 rounded-2xl bg-white border border-amber-200 px-5 py-6 text-center" data-testid="invite-confirm-new">
            <p className="text-lg font-bold text-[var(--fts-text-primary)]">Du bist sofort drin.</p>
            <Link to="/dashboard" data-testid="invite-confirm-new-cta"
              className="mt-5 inline-flex min-h-[52px] items-center justify-center rounded-2xl bg-slate-900 px-6 font-bold text-white">
              Zu deinem PocketCoach →
            </Link>
          </div>
        )}

        {}
        {!checkInbox && <form
          onSubmit={handleSubmit}
          className="mt-10 space-y-4"
          data-testid="invite-form"
        >
          <div>
            <label
              htmlFor="invite-firstname"
              className="block text-sm font-semibold text-[var(--fts-text-primary)]"
            >
              Dein Vorname
            </label>
            <input
              id="invite-firstname"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Vorname"
              maxLength={80}
              autoComplete="given-name"
              className="mt-1 w-full rounded-xl border border-[var(--fts-neutral-200)] bg-white px-4 py-3.5 text-base text-[var(--fts-text-primary)] shadow-sm focus:border-[var(--fts-accent)] focus:outline-none focus:ring-2 focus:ring-[var(--fts-accent)]/20"
              data-testid="invite-firstname-input"
            />
          </div>
          <div>
            <label
              htmlFor="invite-email"
              className="block text-sm font-semibold text-[var(--fts-text-primary)]"
            >
              Deine Mail-Adresse
            </label>
            <input
              id="invite-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="du@beispiel.de"
              required
              autoComplete="email"
              inputMode="email"
              className="mt-1 w-full rounded-xl border border-[var(--fts-neutral-200)] bg-white px-4 py-3.5 text-base text-[var(--fts-text-primary)] shadow-sm focus:border-[var(--fts-accent)] focus:outline-none focus:ring-2 focus:ring-[var(--fts-accent)]/20"
              data-testid="invite-email-input"
            />
          </div>

          {}
          <label className="flex items-start gap-3 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-slate-300 text-[var(--fts-accent)] focus:ring-[var(--fts-accent)]"
              data-testid="invite-consent-checkbox"
            />
            <span>
              Ich möchte Flex4Success nutzen und bin einverstanden, dass Andreas
              mir dazu gelegentlich schreibt.
            </span>
          </label>

          {error && (
            <div
              className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700"
              data-testid="invite-error"
            >
              {error}
            </div>
          )}

          {}
          <button
            type="submit"
            disabled={submitting}
            className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-[var(--fts-accent)] px-6 py-4 text-lg font-bold text-white shadow-lg transition-all hover:opacity-95 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-60"
            data-testid="invite-submit-button"
          >
            {submitting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
                <span>Einen Moment …</span>
              </>
            ) : (
              <>
                <span>Zu deinem PocketCoach →</span>
              </>
            )}
          </button>

          {}
          <div
            className="mt-3 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs text-slate-500"
            data-testid="invite-facts"
          >
            <span className="inline-flex items-center gap-1.5">
              <span className="w-1 h-1 rounded-full bg-amber-500" aria-hidden="true" />
              Kostenlos
            </span>
            <span className="text-slate-300">·</span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-1 h-1 rounded-full bg-amber-500" aria-hidden="true" />
              Du bist sofort drin
            </span>
          </div>
        </form>}

      </div>
    </div>
  );
}
